import React, { useState, useEffect } from 'react';
import { ImageIcon } from 'lucide-react';

/**
 * OptimizedImage Component
 * 
 * A performance-optimized image component that:
 * - Supports lazy loading
 * - Shows a placeholder while loading
 * - Handles error states gracefully
 * - Provides smooth fade-in transitions
 * - Supports responsive images
 * - Can be set to blur/low quality while loading
 * 
 * @param {Object} props
 * @param {string} props.src - Image source URL
 * @param {string} props.alt - Alternative text for the image
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.placeholderSrc - Low-quality placeholder image
 * @param {string} props.placeholderColor - Background color for placeholder
 * @param {boolean} props.lazy - Whether to use lazy loading
 * @param {string} props.objectFit - CSS object-fit property (cover, contain, etc.)
 * @param {Function} props.onLoad - Function to call when image loads
 * @param {Function} props.onError - Function to call when image fails to load
 */
const OptimizedImage = ({
  src,
  alt = '',
  className = '',
  placeholderSrc,
  placeholderColor = '#f3f4f6',
  lazy = true,
  objectFit = 'cover',
  onLoad,
  onError,
  ...rest
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(placeholderSrc || src);

  // Effect to handle image URL changes
  useEffect(() => {
    if (src) {
      // Reset states when src changes
      setIsLoaded(false);
      setHasError(false);
      setCurrentSrc(placeholderSrc || src);
    }
  }, [src, placeholderSrc]);

  const handleImageLoad = () => {
    setIsLoaded(true);
    setCurrentSrc(src);
    if (onLoad) onLoad();
  };

  const handleImageError = (e) => {
    console.warn(`Error loading image: ${src}`);
    setHasError(true);
    if (onError) onError(e);
  };

  // Container style with background color for placeholder
  const containerStyle = {
    backgroundColor: placeholderColor,
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };

  // Basic image style
  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: objectFit,
    transition: 'opacity 0.3s ease-in-out',
    opacity: isLoaded ? 1 : 0.5,
  };

  return (
    <div style={containerStyle} className={className} {...rest}>
      {hasError ? (
        <div className="flex flex-col items-center justify-center h-full w-full p-4 text-center">
          <ImageIcon className="w-8 h-8 mb-2 opacity-50" />
          <span className="text-sm text-gray-500">Image not available</span>
        </div>
      ) : (
        <>
          {/* The actual image (hidden during loading if placeholder available) */}
          <img
            src={src}
            alt={alt}
            style={imageStyle}
            loading={lazy ? "lazy" : "eager"}
            onLoad={handleImageLoad}
            onError={handleImageError}
            role={alt ? "img" : "presentation"}
          />
          
          {/* Low-quality placeholder that shows while the main image loads */}
          {placeholderSrc && !isLoaded && !hasError && (
            <img
              src={placeholderSrc}
              alt=""
              style={{
                ...imageStyle,
                position: 'absolute',
                top: 0,
                left: 0,
                opacity: 0.7,
                filter: 'blur(10px)',
              }}
              aria-hidden="true"
            />
          )}

          {/* Loading indicator for when no placeholder is available */}
          {!placeholderSrc && !isLoaded && !hasError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-pulse flex flex-col items-center">
                <ImageIcon className="w-8 h-8 opacity-30" />
                <span className="text-xs mt-2 opacity-50">Loading</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OptimizedImage; 